<template>
  <label
    class="ui-switch"
    :class="[`ui-switch--${size}`, { 'ui-switch--disabled': disabled }]"
    @click="preventDisabledInteraction"
  >
    <input
      v-bind="$attrs"
      :checked="modelValue"
      data-test-id="ui-switch"
      :disabled="disabled"
      :name="name"
      role="switch"
      type="checkbox"
      @change="change"
    >
    <span
      aria-hidden="true"
      class="ui-switch__track"
      :class="{ 'ui-switch__track--selected': modelValue }"
      ><span class="ui-switch__handle" /></span
    >
    <span v-if="label || $slots.default" class="ui-switch__label"><slot>{{ label }}</slot></span>
  </label>
</template>
<script setup lang="ts">
  defineOptions({ inheritAttrs: false, name: 'UiSwitch' })
  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      label?: string
      modelValue?: boolean
      name?: string
      size?: 'md' | 'sm'
    }>(),
    { modelValue: false, size: 'sm' },
  )
  const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()
  const change = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (props.disabled) {
      input.checked = props.modelValue
      return
    }
    emit('update:modelValue', input.checked)
  }
  const preventDisabledInteraction = (event: MouseEvent) => {
    if (props.disabled) event.preventDefault()
  }
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Checked, disabled and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */

.ui-switch {
 @include typography.font-style('text-sm', 'regular');

 align-items: center;
 color: var(--color-text-text-secondary-700);
 cursor: pointer;
 display: inline-flex;
 gap: 8px;
 position: relative;

}

input {
 height: 1px;
 opacity: 0;
 position: absolute;
 width: 1px;

}

.ui-switch__track {
 background: var(--color-background-bg-tertiary);
 border: 1px solid transparent;
 border-radius: 999px;
 box-shadow: inset 0 0 0 1px #0a0d1208;
 display: flex;
 flex: none;
 padding: 1px;
 transition: background-color 0.2s ease, box-shadow 0.2s ease;

}

.ui-switch--sm .ui-switch__track {
 height: 20px;
 width: 36px;

}

.ui-switch--md .ui-switch__track {
 height: 24px;
 width: 44px;

}

.ui-switch__handle {
 background: var(--color-base-white);
 border-radius: 50%;
 box-shadow: 0 1px 2px var(--color-effects-shadows-shadow-sm-01);
 height: 16px;
 transition: transform 0.2s ease;
 width: 16px;

}

.ui-switch--md .ui-switch__handle {
 height: 20px;
 width: 20px;

}

input:hover:not(:disabled) + .ui-switch__track {
 background: var(--color-gray-light-300);

}

input:focus-visible + .ui-switch__track {
 outline: 2px solid var(--color-effects-focus-rings-focus-ring);
 outline-offset: 2px;

}

.ui-switch__track--selected {
 background: var(--color-background-bg-brand-solid);

}

input:hover:not(:disabled) + .ui-switch__track--selected {
 background: var(--color-background-bg-brand-solid-hover);

}

.ui-switch--sm .ui-switch__track--selected .ui-switch__handle {
 transform: translateX(16px);

}

.ui-switch--md .ui-switch__track--selected .ui-switch__handle {
 transform: translateX(20px);

}

input:active:not(:disabled) + .ui-switch__track .ui-switch__handle {
 transform: scale(0.92);

}

.ui-switch--sm input:active:not(:disabled) + .ui-switch__track--selected .ui-switch__handle {
 transform: translateX(16px) scale(0.92);

}

.ui-switch--md input:active:not(:disabled) + .ui-switch__track--selected .ui-switch__handle {
 transform: translateX(20px) scale(0.92);

}

input:disabled + .ui-switch__track {
 background: var(--color-background-bg-disabled);

}

.ui-switch--disabled {
 color: var(--color-text-text-disabled);
 cursor: default;
 pointer-events: none;

}

</style>
